import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import useListHook from '../../hooks/lists/useListHook';
import DataTable from '../DataTable/DataTable';
import { rowsPerPageOptions } from '../../constants/pagination';
import { PNSnackbar } from '../Snackbar/PNSnackbar';

const FullList = ({
  columns,
  checkboxColumn = false,
  checkboxTools = null,
  showToolbarIcons,
  initialValues,
  getData,
  data,
  loading,
  error,
  processData = () => null,
  onLineClick = () => null,
  onFilterOpen = null,
  toolbarComponents = [],
  errorMessage = null,
  setErrorMessage = () => null,
  successMessage = null,
  setSuccessMessage = () => null,
  refresh = null,
  setRefresh = () => null,
  setSearchQuery = () => null,
  sessionFilterStorageName = null,
}) => {
  const {
    totalCount,
    columnsFilter,
    filterOptions,
    fulltextFilter,
    orderByColumn,
    orderDirection,
    page,
    rows,
    rowsPerPage,
    setColumnsFilter,
    setFilterOptions,
    setFulltextFilter,
    setOrderByColumn,
    setOrderDirection,
    setPage,
    setRows,
    setRowsPerPage,
    setTotalCount,
  } = useListHook(initialValues);

  const refreshData = () => {
    const query = {
      page: page,
      size: rowsPerPage,
      sort: {
        type: orderDirection,
        column: orderByColumn,
      },
      fulltext: fulltextFilter || undefined,
      filters: [...prepareFilters(columnsFilter)],
    };

    setSearchQuery(query);
    saveFilterIntoSession(query);

    (async () => {
      await getData(query);
    })();
  };

  const saveFilterIntoSession = (query) => {
    if (sessionFilterStorageName != null && query != null && query.filters != null) {
      const queryToStore = {
        filterOptions: filterOptions,
        page: query.page,
        size: query.size,
        order: query.sort?.type,
        orderBy: query.sort?.column,
        fulltext: query.fulltext,
        columnsFilter: columnsFilter,
      };
      sessionStorage.setItem(sessionFilterStorageName, JSON.stringify(queryToStore));
    }
  };

  const prepareFilters = (filters) => {
    return Object.keys(filters).map((key) => {
      if (filterOptions[key]) {
        const allIds = filterOptions[key]?.map((line) => line.id);
        return {
          column: key,
          values: allIds?.filter((x) => !filters[key].includes(x)),
        };
      } else {
        return {
          column: key,
          values: filters[key],
        };
      }
    });
  };

  useEffect(() => {
    refreshData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getData, fulltextFilter, columnsFilter, orderDirection, orderByColumn, page, rowsPerPage]);

  useEffect(() => {
    if (refresh) {
      refreshData();
      setRefresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh]);

  useEffect(() => {
    if (data) {
      const processed = processData(data);
      setRows(processed?.data || []);
      setTotalCount(processed?.totalCount || 0);
    }
  }, [data, processData, setRows, setTotalCount]);

  const onFilterOpenWrapper = useCallback(
    async (column) => {
      if (onFilterOpen) {
        const output = await onFilterOpen(column);
        setFilterOptions((currentValues) => {
          return {
            ...currentValues,
            [column]: output,
          };
        });
        return output;
      } else {
        return null;
      }
    },
    [onFilterOpen, setFilterOptions],
  );

  return (
    <>
      {errorMessage && (
        <PNSnackbar
          type={'error'}
          onClose={(event, reason) => {
            if (reason !== 'clickaway') {
              setErrorMessage(null);
            }
          }}
          open={true}>
          {errorMessage}
        </PNSnackbar>
      )}
      {successMessage && (
        <PNSnackbar
          type={'success'}
          onClose={() => {
            setSuccessMessage(null);
          }}
          open={true}
          autoHideDuration={6000}>
          {successMessage}
        </PNSnackbar>
      )}
      <DataTable
        lineHover
        checkboxColumn={checkboxColumn}
        checkboxTools={checkboxTools}
        onLineClick={onLineClick}
        columns={columns}
        totalCount={totalCount}
        rowsPerPageOptions={rowsPerPageOptions}
        data={rows || []}
        loading={loading}
        error={error}
        fulltextFilter={fulltextFilter}
        setFulltextFilter={setFulltextFilter}
        columnsFilter={columnsFilter}
        setColumnsFilter={setColumnsFilter}
        orderDirection={orderDirection}
        setOrderDirection={setOrderDirection}
        orderByColumn={orderByColumn}
        setOrderByColumn={setOrderByColumn}
        page={page}
        setPage={setPage}
        rowsPerPage={rowsPerPage}
        setRowsPerPage={setRowsPerPage}
        onFilterOpen={onFilterOpenWrapper}
        customTools={showToolbarIcons && toolbarComponents}
      />
    </>
  );
};

FullList.propTypes = {
  columns: PropTypes.array,
  checkboxColumn: PropTypes.bool,
  checkboxTools: PropTypes.array,
  showToolbarIcons: PropTypes.bool,
  initialValues: PropTypes.object,
  getData: PropTypes.func,
  data: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  loading: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  processData: PropTypes.func,
  onLineClick: PropTypes.func,
  onFilterOpen: PropTypes.func,
  toolbarComponents: PropTypes.array,
  errorMessage: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  setErrorMessage: PropTypes.func,
  successMessage: PropTypes.node,
  setSuccessMessage: PropTypes.func,
  refresh: PropTypes.bool,
  setRefresh: PropTypes.func,
  setSearchQuery: PropTypes.func,
  sessionFilterStorageName: PropTypes.string,
};

export default FullList;
